/* eslint-disable unused-imports/no-unused-vars */
import { createSelector } from 'reselect';
import type { State } from '../services/reducers';
import type {
  BundledProduct,
  CartQuantities,
  Favorites,
  StateProductData,
  VariantData,
} from '../sharedTypes';

export type WithSelectedProductLinkInjectedProps = {
  selectedProductLink: string | null;
  accordionInGrid: boolean;
};

export const productTypeEnum = {
  IN_STOCK: 'IN_STOCK',
  SPONSORED: 'SPONSORED',
  GWP: 'GWP',
  SOLD_OUT: 'SOLD_OUT',
  DEFAULT: 'DEFAULT',
};
type SelectProductsProps = {
  type: string;
  selectedCategoryId: string | null;
};

const mapProductsToGwp = (
  sponsoredProductIds: string[],
  searchResults: string[],
  products: Record<string, StateProductData>,
  variants: Record<string, VariantData>,
  variantIdsBeingUpdated: Set<string>,
  favorites: Favorites,
  variantQuantities: CartQuantities,
  prevPurchasedVariantQuantities: CartQuantities,
) => {
  return sponsoredProductIds
    .filter((productId) => !searchResults || searchResults.includes(productId))
    .map((productId) =>
      bundleProduct(
        productId,
        products,
        variants,
        variantIdsBeingUpdated,
        favorites,
        variantQuantities,
        prevPurchasedVariantQuantities,
        productTypeEnum.GWP,
      ),
    )
    .filter((product) => {
      return product.gwp;
    })
    .sort((a, b) => a.gwpThreshold - b.gwpThreshold);
};

/**
 * Higher order function that returns a selector that can be shared and retain memoization.
 * See https://redux.js.org/recipes/computing-derived-data#sharing-selectors-across-multiple-components
 * @returns {Array<BundledProduct>}selector that filters products by type and bundles them with their variants
 */
export function makeSelectProducts(): (
  state: State,
  products: SelectProductsProps,
) => Array<BundledProduct> {
  return createSelector(
    (state, props) => state.productsState.products,
    (state, props) => state.productsState.variants,
    (state, props) => state.productsState.displayedProductIds,
    (state, props) => state.productsState.sponsoredProductIds,
    (state, props) => state.cartState.variantIdsBeingUpdated,
    (state, props) => state.productsState.favorites,
    (state, props) => state.cartState.variantQuantities,
    (state, props) => props.type,
    (state, props) => props.selectedCategoryId,
    (state, props) => state.cartState.prevPurchasedVariantQuantities,
    (state, props) => state.searchState.searchResults,
    (
      products,
      variants,
      displayedProductIds,
      sponsoredProductIds,
      variantIdsBeingUpdated,
      favorites,
      variantQuantities,
      type,
      selectedCategoryId,
      prevPurchasedVariantQuantities,
      searchResults,
    ) => {
      const filteredDisplayProductIds = searchResults || displayedProductIds;
      switch (type) {
        case productTypeEnum.IN_STOCK:
          return filteredDisplayProductIds
            .map((productId) =>
              bundleProduct(
                productId,
                products,
                variants,
                variantIdsBeingUpdated,
                favorites,
                variantQuantities,
                prevPurchasedVariantQuantities,
                productTypeEnum.IN_STOCK,
              ),
            )
            .filter((product) => {
              const { sponsored, isSoldOutOnFetch, isFavorite, isProductAtMaxedCartLimit, gwp } =
                product;
              if (isProductAtMaxedCartLimit) {
                return false;
              }
              if (!searchResults) {
                if (selectedCategoryId === 'mycart')
                  return isSomeVariantInCart(product) && !sponsored && !isSoldOutOnFetch && !gwp;
                if (selectedCategoryId === 'myfavorites') return isFavorite && !isSoldOutOnFetch;
              }
              return !sponsored && !isSoldOutOnFetch;
            });
        case productTypeEnum.SPONSORED:
          return sponsoredProductIds
            .filter((productId) => !searchResults || searchResults.includes(productId))
            .map((productId) =>
              bundleProduct(
                productId,
                products,
                variants,
                variantIdsBeingUpdated,
                favorites,
                variantQuantities,
                prevPurchasedVariantQuantities,
                productTypeEnum.SPONSORED,
              ),
            )
            .filter((product) => {
              if (product.isProductAtMaxedCartLimit) {
                return false;
              }
              return product.sponsored && !product.gwp;
            });
        case productTypeEnum.SOLD_OUT:
          return filteredDisplayProductIds
            .map((productId) =>
              bundleProduct(
                productId,
                products,
                variants,
                variantIdsBeingUpdated,
                favorites,
                variantQuantities,
                prevPurchasedVariantQuantities,
                productTypeEnum.SOLD_OUT,
              ),
            )
            .filter((product) => {
              const { isSoldOutOnFetch, sponsored, isFavorite, isProductAtMaxedCartLimit } =
                product;
              if (isProductAtMaxedCartLimit) {
                return false;
              }
              if (!searchResults) {
                if (selectedCategoryId === 'mycart')
                  return isSomeVariantInCart(product) && !sponsored && isSoldOutOnFetch;
                if (selectedCategoryId === 'myfavorites') return isFavorite && isSoldOutOnFetch;
              }
              return isSoldOutOnFetch && !sponsored;
            });
        case productTypeEnum.GWP:
          return mapProductsToGwp(
            sponsoredProductIds,
            searchResults,
            products,
            variants,
            variantIdsBeingUpdated,
            favorites,
            variantQuantities,
            prevPurchasedVariantQuantities,
          );
        default:
          return displayedProductIds
            .map((productId) =>
              bundleProduct(
                productId,
                products,
                variants,
                variantIdsBeingUpdated,
                favorites,
                variantQuantities,
                prevPurchasedVariantQuantities,
                productTypeEnum.DEFAULT,
              ),
            )
            .filter((product) => {
              const { sponsored, isProductAtMaxedCartLimit } = product;
              if (isProductAtMaxedCartLimit) {
                return false;
              }
              return !sponsored;
            });
      }
    },
  );
}

export function makeSelectGwpProducts(): (state: State) => Array<BundledProduct> {
  return createSelector(
    (state) => state.productsState.products,
    (state) => state.productsState.variants,
    (state) => state.productsState.sponsoredProductIds,
    (state) => state.cartState.variantIdsBeingUpdated,
    (state) => state.productsState.favorites,
    (state) => state.cartState.variantQuantities,
    (state) => state.cartState.prevPurchasedVariantQuantities,
    (state) => state.searchState.searchResults,
    (
      products,
      variants,
      sponsoredProductIds,
      variantIdsBeingUpdated,
      favorites,
      variantQuantities,
      prevPurchasedVariantQuantities,
      searchResults,
    ) => {
      return mapProductsToGwp(
        sponsoredProductIds,
        searchResults,
        products,
        variants,
        variantIdsBeingUpdated,
        favorites,
        variantQuantities,
        prevPurchasedVariantQuantities,
      );
    },
  );
}

type GetSelectedProductIdProps = { selectedProductLink: string | null };

const getSelectedProductId: (state: State, products: GetSelectedProductIdProps) => string | null =
  createSelector(
    (state: State, props: GetSelectedProductIdProps) => props.selectedProductLink,
    (state: State) => state.productsState.products,
    (selectedProductLink, products) => {
      for (const id in products) {
        const product = products[id];
        if (product.shareUrl === selectedProductLink) return id;
      }
      return null;
    },
  );

export const getSelectedProduct: (
  state: State,
  products: {
    selectedProductLink: string | null;
    type: string;
    selectedCategoryId: string | null;
  },
) => BundledProduct | null = createSelector(
  getSelectedProductId,
  makeSelectProducts(),
  (selectedProductId, products) =>
    products.find((product) => product.id === selectedProductId) || null,
);

export const getDetailIndex: (
  state: State,
  products: {
    type: string;
    selectedCategoryId: string | null;
  } & WithSelectedProductLinkInjectedProps,
) => number | null = createSelector(
  makeSelectProducts(),
  getSelectedProductId,
  (state) => state.viewState.viewSize,
  (state, props) => props.accordionInGrid,
  (products, selectedProductId, viewSize, accordionInGrid) => {
    if (!accordionInGrid) return null;
    if (selectedProductId === null) return null;
    let itemsPerRow;
    if (viewSize === 'small') {
      itemsPerRow = 2;
    } else if (viewSize === 'medium') {
      itemsPerRow = 3;
    } else {
      itemsPerRow = 4;
    }
    const indexOfSelectedProduct = products.findIndex(
      (product) => product.id === selectedProductId,
    );
    if (indexOfSelectedProduct === -1) return null;
    return Math.floor(indexOfSelectedProduct / itemsPerRow) * itemsPerRow;
  },
);

export const getTopAccordionProduct: (
  state: State,
  products: WithSelectedProductLinkInjectedProps,
) => BundledProduct | null = createSelector(
  (state, props) => state.productsState.products,
  (state, props) => state.productsState.variants,
  (state, props) => state.productsState.favorites,
  (state, props) => state.cartState.variantIdsBeingUpdated,
  (state, props) => state.cartState.variantQuantities,
  (state, props) => props.selectedProductLink,
  (state, props) => props.accordionInGrid,
  (state, props) => state.cartState.prevPurchasedVariantQuantities,
  (
    products,
    variants,
    favorites,
    variantIdsBeingUpdated,
    variantQuantities,
    selectedProductLink,
    accordionInGrid,
    prevPurchasedVariantQuantities,
  ) => {
    if (accordionInGrid) return null;
    let selectedProductId = null;
    for (const id in products) {
      if (products[id].shareUrl === selectedProductLink) {
        selectedProductId = id;
        break;
      }
    }
    if (selectedProductId === null) return null;
    return bundleProduct(
      selectedProductId,
      products,
      variants,
      variantIdsBeingUpdated,
      favorites,
      variantQuantities,
      prevPurchasedVariantQuantities,
    );
  },
);

export const selectHasFavorites: (state: State) => boolean = createSelector(
  (state) => state.productsState.favorites,
  (favorites) => Object.values(favorites).filter((isFavorite) => isFavorite).length > 0,
);

function bundleProduct(
  productId: string,
  products: { [id: string]: StateProductData },
  variants: { [id: string]: VariantData },
  variantIdsBeingUpdated: Set<string>,
  favorites: Favorites,
  variantQuantities: CartQuantities,
  prevPurchasedVariantQuantities: CartQuantities,
  productType: string,
): BundledProduct {
  let variantsPrevPurchasedTotal = 0;

  const productVariants = products[productId].variantIds.map((variantId) => {
    const prevPurchasedCount = prevPurchasedVariantQuantities[variantId] || 0;
    const { cartLimit } = variants[variantId];
    const newCartLimit = cartLimit - prevPurchasedCount;
    const isVariantAtPurchaseLimit = newCartLimit === 0;

    if (isVariantAtPurchaseLimit) {
      variantsPrevPurchasedTotal++;
    }

    return {
      ...variants[variantId],
      areCartOperationsDisabled: variantIdsBeingUpdated.has(variantId),
      id: variantId,
      quantityUpdating: variantIdsBeingUpdated.has(variantId), // determines button loading state
      quantity: variantQuantities[variantId] || 0,
      isPreviouslyPurchased: prevPurchasedCount > 0,
      cartLimit: newCartLimit,
      isVariantAtPurchaseLimit,
    };
  });

  const bundledProduct = {
    ...products[productId],
    variants: productVariants,
    id: productId,
    isFavorite: favorites[productId] === true,
    soldOut: productVariants.every((variant) => variant.soldOut),
    isSoldOutOnFetch: productVariants.every((variant) => variant.isSoldOutOnFetch),
    isProductAtMaxedCartLimit: variantsPrevPurchasedTotal === productVariants.length,
    productType,
  };

  return bundledProduct;
}

function isSomeVariantInCart(product: BundledProduct): boolean {
  return product.variants.some((variant) => variant.quantity > 0);
}

export const selectIsVariantToRemoveSponsored: (state: State) => boolean = createSelector(
  (state) => state.productsState.variantToRemove,
  (state) => state.productsState.variants,
  (state) => state.productsState.products,
  (variantToRemove, variants, products) => {
    if (variantToRemove === null) return false;
    const productId = variants[variantToRemove].parentId;
    return products[productId].sponsored;
  },
);

export const selectProduct: (state: State, products: { productId: string }) => StateProductData =
  createSelector(
    (state, props) => state.productsState.products,
    (state, props) => props.productId,
    (products, productId) => products[productId],
  );

export const selectVariant: (state: State, products: { variantId: string }) => VariantData =
  createSelector(
    (state, props) => state.productsState.variants,
    (state, props) => props.variantId,
    (variants, variantId) => variants[variantId],
  );

export const selectIsProductInStock: (state: State, products: { productId: string }) => boolean =
  createSelector(
    selectProduct,
    (state, props) => state.productsState.variants,
    (product, variants) => product.variantIds.some((variantId) => !variants[variantId].soldOut),
  );

export const selectProductFromVariantId: (
  state: State,
  products: { variantId: string },
) => StateProductData = createSelector(
  selectVariant,
  (state) => state.productsState.products,
  (variant, products) => {
    if (variant) {
      return products[variant.parentId];
    }
    return null;
  },
);

export const selectVariantQuantity: (state: State, products: { variantId: string }) => number =
  createSelector(
    (state, props) => props.variantId,
    (state, props) => state.cartState.variantQuantities,
    (variantId, variantQuantities) => variantQuantities[variantId] || 0,
  );

export const selectProductIds: (state: State) => Array<string> = createSelector(
  (state) => state.productsState.displayedProductIds,
  (state) => state.productsState.sponsoredProductIds,
  (state) => state.productsState.products,
  (state) => state.productsState.variants,
  (state) => state.searchState.searchResults,
  (displayedProductIds, sponsoredProductIds, products, variants, searchResults) => {
    const inStockProductIds = [];
    const soldOutProductIds = [];
    const displayedProducts = searchResults || displayedProductIds;
    displayedProducts.forEach((productId) => {
      const product = products[productId];
      if (product) {
        const isProductInStock = product.variantIds.some(
          (variantId) => !variants[variantId].isSoldOutOnFetch,
        );
        const isProductSponsored = product.sponsored;
        if (!isProductSponsored) {
          if (isProductInStock) {
            inStockProductIds.push(productId);
          } else {
            soldOutProductIds.push(productId);
          }
        }
      }
    });

    return {
      inStock: [...inStockProductIds],
      sponsored: sponsoredProductIds,
      oos: [...soldOutProductIds],
    };
  },
);
